/**
 * Pure-logic helpers for the Prompt Apply Dialog (#151).
 *
 * Extracted so they can be unit-tested without JSDOM/RTL — same pattern
 * used by PromptEditor.helpers.ts. The dialog uses these to:
 *
 *   - substitute `{{var}}` placeholders in the prompt body with live
 *     user values (matches the backend `POST /:command/apply` behaviour),
 *   - identify required variables the user hasn't filled in yet,
 *   - decide whether the Apply button should be enabled.
 *
 * IMPORTANT — substitution semantics:
 *
 * The server's `/apply` endpoint switched from RegExp-based replacement
 * to literal split/join (fix #629). Variable names are also constrained
 * by PromptVariableSchema to `[a-zA-Z_][a-zA-Z0-9_]*`, but historical
 * data may pre-date #628 and contain arbitrary characters. The preview
 * helper here matches the server-side semantics: literal `{{name}}`
 * token substitution with NO regex construction and NO String.replace
 * backreference expansion. This guarantees the client-side live preview
 * is byte-identical to whatever `/apply` will return when the user
 * clicks Apply.
 *
 * It is deliberately a tiny re-implementation of the server-side
 * `literalReplaceAll` so the two stay in lock-step.
 */

import type { PromptVariableEntry } from '../variable-editor';

// ─── Re-export of the canonical PromptVariableEntry shape ──────────────────
// Re-exported here so the apply-dialog file can import everything from
// one place without having to know about the studio/variable-editor module.
export type { PromptVariableEntry } from '../variable-editor';

/**
 * Literal-string `replaceAll` mirroring `literalReplaceAll` on the server
 * (packages/api/src/routes/v1/prompt-templates.ts:724). Never constructs
 * a RegExp, never uses `String.replace(string, string)` replacement-
 * string semantics. Empty needle returns the input unchanged.
 */
export function literalReplaceAll(haystack: string, needle: string, value: string): string {
  if (needle.length === 0) return haystack;
  return haystack.split(needle).join(value);
}

/**
 * Substitute `{{var}}` placeholders in `template` with values from
 * `values` (falling back to each variable's `default`). The result is
 * byte-identical to what the server's `/apply` endpoint would return.
 *
 * Variables not declared on the template but provided as a free-form
 * key in `values` are ALSO substituted — this matches the server's
 * second loop, which expands `Object.entries(variables)` after the
 * declared-variable loop. Pre-fix this was a subtle attack surface
 * (#629), but with literal-string substitution there is no way for
 * an arbitrary key to bypass intent.
 */
export function substitutePromptBody(
  template: string,
  variables: ReadonlyArray<PromptVariableEntry>,
  values: Readonly<Record<string, string>>,
): string {
  let out = template;
  // 1) Declared variables — fall back to default if no value provided.
  for (const v of variables) {
    const raw = values[v.name];
    const value = String(raw ?? v.default ?? '');
    out = literalReplaceAll(out, `{{${v.name}}}`, value);
  }
  // 2) Free-form `values` keys that aren't declared on the template.
  //    Matches the server's behaviour: late-bound expansion is allowed.
  for (const [key, value] of Object.entries(values)) {
    if (variables.some((v) => v.name === key)) continue; // already done
    out = literalReplaceAll(out, `{{${key}}}`, String(value));
  }
  return out;
}

/**
 * Names of required variables that haven't been provided AND have no
 * non-empty default. Mirrors the server's `missingRequired` check at
 * prompt-templates.ts:644-649 (`v.required && !variables[v.name] && !v.default`).
 *
 * Used to:
 *   - mark required-but-empty inputs with destructive styling
 *   - disable the Apply button until all required vars are filled
 *   - render a leading "N required missing" badge in the dialog
 */
export function missingRequiredVariables(
  variables: ReadonlyArray<PromptVariableEntry>,
  values: Readonly<Record<string, string>>,
): string[] {
  const out: string[] = [];
  for (const v of variables) {
    if (!v.required) continue;
    const supplied = values[v.name];
    if ((supplied === undefined || supplied === '') && !v.default) {
      out.push(v.name);
    }
  }
  return out;
}

/**
 * Whether the Apply button should be enabled. False when:
 *   - the template body is empty (nothing to apply), OR
 *   - one or more required variables are still missing.
 */
export function canApply(
  template: string,
  variables: ReadonlyArray<PromptVariableEntry>,
  values: Readonly<Record<string, string>>,
): boolean {
  if (!template || template.trim().length === 0) return false;
  return missingRequiredVariables(variables, values).length === 0;
}

/**
 * Initial values map for the dialog — uses each variable's `default`
 * so the live preview can render a sensible body immediately when the
 * dialog opens. The user can overwrite any field.
 */
export function initialValues(
  variables: ReadonlyArray<PromptVariableEntry>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of variables) {
    if (v.default !== undefined && v.default !== '') {
      out[v.name] = v.default;
    }
  }
  return out;
}
